// Sprint 3 Slice 1 — edit-mode toolbar + workflow controller.
//
// Activated by ?edit=1 in the URL. Mounts a small toolbar top-right with
// two actions:
//   • + Hotspot — push a new info hotspot into the current room at the
//                 camera's current yaw/pitch, then open the EditorPanel
//                 so the operator can fill in the label/description.
//   • Exportar rooms.json — serialize the in-memory RoomsConfig and
//                           trigger a browser download.
//
// All mutations happen on the live RoomsConfig held by App; persistence
// is the operator's job (copy the downloaded JSON into src/data/).

import type { Hotspot, Room, RoomsConfig, Variant } from './types';
import { findNavProblems } from './editorProblems';
import {
  clearAll as clearAllGeneratedVariants,
  getProjectId,
  setProjectId,
  importFromServer,
} from './generatedVariantStore';
import { showToast } from '../ui/toast';

export interface EditorModeOptions {
  /** Live RoomsConfig — mutated in place. App holds the same reference. */
  config: RoomsConfig;
  /** Reads the camera's current look so + Hotspot drops at the aim point. */
  getCameraLook: () => { yaw: number; pitch: number };
  /** Returns the room the user is currently looking at. */
  getActiveRoom: () => Room | null;
  /** Returns the variant currently displayed in the active room — used by the
   *  "Salvar no projeto" promote flow. */
  getActiveVariant: () => { variantId: string; panoramaUrl: string; name: string; generatedStyle?: string; generatedPrompt?: string } | null;
  /** Fired after a hotspot is added so App can refresh the layer. */
  onHotspotAdded: (hotspot: Hotspot, room: Room) => void;
  /**
   * Fired when the operator drags the compass / yawOffset slider. App
   * mutates `room.floorplan.yawOffsetDeg` upstream and re-renders the
   * floorplan + dollhouse so the change reads live.
   */
  onRoomYawOffsetChange: (room: Room, yawOffsetDeg: number) => void;
  /**
   * Returns the live Variant object being displayed, so the alignment slider
   * can read and mutate it. `getActiveVariant` above returns a flattened
   * projection built for the promote flow and cannot be written through.
   */
  getActiveVariantObject: () => { room: Room; variant: Variant } | null;
  /**
   * Fired while the operator drags the panorama-alignment slider. App pushes
   * the value straight into the renderer so the rotation previews live, and
   * mutates `variant.yawOffsetDeg` so it persists with the rest of the config.
   */
  onVariantYawOffsetChange: (room: Room, variant: Variant, yawOffsetDeg: number) => void;
  /**
   * True in Demo Studio's ?embed=1 mode. The portal owns persistence and the
   * asset pipeline there, so the toolbar drops every action that assumes a
   * local operator with a filesystem and this repo's own /api routes:
   * Export (downloads a file the admin has nowhere to put), Save-to-Project
   * (opens a PR against THIS repo, not the client's tour), and the localStorage
   * variant tools (scoped to the bundled demo).
   */
  embedded?: boolean;
  /** Embed mode only — operator asked for an immediate push to the portal. */
  onSaveRequested?: () => void;
}

export class EditorMode {
  private readonly opts: EditorModeOptions;
  private readonly active: boolean;
  private toolbar: HTMLElement | null = null;
  private navProblemsBtn: HTMLButtonElement | null = null;
  /** Compass slider — re-bound to the active room's yawOffsetDeg on room change. */
  private compassSlider: HTMLInputElement | null = null;
  private compassReadout: HTMLElement | null = null;
  /** Panorama-alignment slider — re-bound to the active VARIANT on every variant change. */
  private panoYawSlider: HTMLInputElement | null = null;
  private panoYawReadout: HTMLElement | null = null;
  /** "+ Hotspot" → click on canvas places at the picked yaw/pitch. */
  private placementArmed = false;
  private addHotspotBtn: HTMLButtonElement | null = null;
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.placementArmed) {
      e.preventDefault();
      this.disarmPlacement();
    }
  };

  constructor(opts: EditorModeOptions) {
    this.opts = opts;
    // ?embed=1 implies edit mode: placing and correcting hotspots is the only
    // reason the portal embeds this viewer at all.
    this.active = isEditUrlFlagSet() || opts.embedded === true;
  }

  isActive(): boolean {
    return this.active;
  }

  /** Mount the toolbar + apply the body class. Idempotent. */
  attach(): void {
    if (!this.active || this.toolbar) return;
    document.body.classList.add('is-editing');
    this.toolbar = document.createElement('div');
    this.toolbar.id = 'editor-toolbar';
    this.toolbar.className = 'editor-toolbar';
    const shared = `
      <button type="button" class="editor-toolbar__btn" data-action="add-hotspot">
        <span aria-hidden="true">＋</span> Hotspot
      </button>
      <label class="editor-toolbar__compass" title="Rotaciona o plano + dollhouse para alinhar com a panorâmica">
        <span aria-hidden="true">🧭</span>
        <input type="range" min="-180" max="180" step="1" value="0" data-action="compass" />
        <span class="editor-toolbar__compass-value" data-readout="compass">0°</span>
      </label>
      <label class="editor-toolbar__compass" title="Gira ESTA variante para alinhar com o Antes. Use quando o render do 3D aponta para outra direção que a foto da obra.">
        <span aria-hidden="true">🔄</span>
        <input type="range" min="-180" max="180" step="1" value="0" data-action="pano-yaw" />
        <span class="editor-toolbar__compass-value" data-readout="pano-yaw">0°</span>
      </label>
      <button type="button" class="editor-toolbar__btn editor-toolbar__btn--warn" data-action="nav-problems" hidden></button>
    `;
    this.toolbar.innerHTML = this.opts.embedded
      ? `
      ${shared}
      <button type="button" class="editor-toolbar__btn" data-action="save-embed" title="Envia as alterações ao portal agora (elas também são salvas automaticamente)">
        💾 Salvar no portal
      </button>
    `
      : `
      ${shared}
      <button type="button" class="editor-toolbar__btn" data-action="export">
        Exportar rooms.json
      </button>
      <button type="button" class="editor-toolbar__btn editor-toolbar__btn--danger" data-action="clear-generated" title="Remove variantes geradas por IA do localStorage (não afeta rooms.json)">
        Limpar geradas
      </button>
      <button type="button" class="editor-toolbar__btn" data-action="copy-project-id" title="Copie este ID para importar variantes em outro dispositivo">
        Copiar ID do projeto
      </button>
      <button type="button" class="editor-toolbar__btn" data-action="import-variants" title="Cole o ID de outro dispositivo para importar variantes geradas">
        Importar variantes
      </button>
      <button type="button" class="editor-toolbar__btn" data-action="promote-variant" title="Promove a variante AI ativa para rooms.json via PR no GitHub (visível em todos os dispositivos após merge)">
        📤 Salvar no projeto
      </button>
      <button type="button" class="editor-toolbar__btn" data-action="analyze-photo" title="Faça upload de uma foto de outro ambiente e a IA gera um stub de Room (nome, subtítulo, narração, preset sugerido)">
        📸 Analisar foto
      </button>
    `;
    document.body.appendChild(this.toolbar);
    this.compassSlider = this.toolbar.querySelector('[data-action="compass"]') as HTMLInputElement;
    this.compassReadout = this.toolbar.querySelector('[data-readout="compass"]') as HTMLElement;
    this.addHotspotBtn = this.toolbar.querySelector('[data-action="add-hotspot"]') as HTMLButtonElement;
    this.panoYawSlider = this.toolbar.querySelector('[data-action="pano-yaw"]') as HTMLInputElement;
    this.panoYawReadout = this.toolbar.querySelector('[data-readout="pano-yaw"]') as HTMLElement;
    this.navProblemsBtn = this.toolbar.querySelector('[data-action="nav-problems"]') as HTMLButtonElement;
    this.compassSlider.addEventListener('input', () => this.handleCompassInput());
    this.panoYawSlider.addEventListener('input', () => this.handlePanoYawInput());
    this.toolbar.addEventListener('click', (e) => {
      const action = (e.target as HTMLElement).closest('[data-action]')?.getAttribute('data-action');
      if (action === 'add-hotspot') this.togglePlacement();
      else if (action === 'nav-problems') this.reportNavProblems();
      else if (action === 'save-embed') this.saveToPortal();
      else if (action === 'export') this.exportRoomsJson();
      else if (action === 'clear-generated') this.clearGeneratedVariants();
      else if (action === 'copy-project-id') void this.copyProjectId();
      else if (action === 'import-variants') void this.importVariants();
      else if (action === 'promote-variant') void this.promoteActiveVariant();
      else if (action === 'analyze-photo') void this.analyzePhotoFlow();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.syncCompassFromRoom();
    this.refreshNavProblems();
    showToast(
      this.opts.embedded
        ? 'Editor conectado ao portal — clique + Hotspot, depois clique no panorama para posicionar'
        : 'Edit mode ativo (?edit=1) — clique + Hotspot, depois clique no panorama para posicionar',
      { tone: 'info' },
    );
  }

  /** App queries this before its canvas-tap picker fires its normal pick flow. */
  isPlacementArmed(): boolean {
    return this.placementArmed;
  }

  /**
   * App calls this with the yaw/pitch derived from a canvas click (via
   * camera.screenToWorld → vec3ToYawPitch) while placement is armed.
   * Creates the new hotspot, disarms placement, and fires onHotspotAdded so
   * App can refresh the layer + open the property panel.
   */
  placeHotspotAt(yaw: number, pitch: number, distance = 12): void {
    if (!this.placementArmed) return;
    const room = this.opts.getActiveRoom();
    if (!room) {
      showToast('Edit: nenhuma sala ativa', { tone: 'error' });
      this.disarmPlacement();
      return;
    }
    const hotspot: Hotspot = {
      id: `hs-${Date.now().toString(36)}`,
      type: 'info',
      label: 'Novo hotspot',
      yaw: round(yaw, 1),
      pitch: round(pitch, 1),
      distance: round(distance, 1),
      description: '',
    };
    room.hotspots.push(hotspot);
    this.disarmPlacement();
    this.opts.onHotspotAdded(hotspot, room);
  }

  /**
   * App calls this after a room change so the compass slider reflects the
   * new active room's yawOffsetDeg. Without it, the slider would still show
   * the previous room's value after navigation.
   */
  syncCompassFromRoom(): void {
    if (!this.compassSlider || !this.compassReadout) return;
    const room = this.opts.getActiveRoom();
    const offset = room?.floorplan?.yawOffsetDeg ?? 0;
    this.compassSlider.value = String(offset);
    this.compassReadout.textContent = `${offset}°`;
    // Hide the compass control entirely for rooms with no floorplan to
    // rotate (the schema allows missing `floorplan`).
    this.compassSlider.disabled = !room?.floorplan;
  }

  private handleCompassInput(): void {
    if (!this.compassSlider || !this.compassReadout) return;
    const room = this.opts.getActiveRoom();
    if (!room?.floorplan) return;
    const value = Number(this.compassSlider.value);
    if (!Number.isFinite(value)) return;
    this.compassReadout.textContent = `${value}°`;
    this.opts.onRoomYawOffsetChange(room, value);
  }

  /**
   * App calls this on every variant change so the alignment slider shows the
   * variant actually on screen. Without it the operator would drag a control
   * still holding the previous variant's value and silently rotate the wrong
   * panorama — the same bug syncCompassFromRoom exists to prevent for rooms.
   */
  syncPanoramaYawFromVariant(): void {
    if (!this.panoYawSlider || !this.panoYawReadout) return;
    const active = this.opts.getActiveVariantObject();
    const offset = active?.variant.yawOffsetDeg ?? 0;
    this.panoYawSlider.value = String(offset);
    this.panoYawReadout.textContent = `${offset}°`;
    // A gsplat / glTF variant has real geometry and is not aligned by shifting
    // an equirect's UVs, so the control would do nothing there.
    const kind = active?.variant.renderer ?? active?.room.renderer ?? 'panorama';
    this.panoYawSlider.disabled = !active || kind !== 'panorama';
  }

  private handlePanoYawInput(): void {
    if (!this.panoYawSlider || !this.panoYawReadout) return;
    const active = this.opts.getActiveVariantObject();
    if (!active) return;
    const value = Number(this.panoYawSlider.value);
    if (!Number.isFinite(value)) return;
    this.panoYawReadout.textContent = `${value}°`;
    this.opts.onVariantYawOffsetChange(active.room, active.variant, value);
  }

  detach(): void {
    document.body.classList.remove('is-editing');
    document.body.classList.remove('is-placing-hotspot');
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.toolbar) {
      this.toolbar.remove();
      this.toolbar = null;
    }
  }

  // ── Internals ─────────────────────────────────────────────────

  /** Toggles placement-armed mode. When armed, the next canvas click drops
   *  the new hotspot at the picked yaw/pitch instead of the camera aim. */
  private togglePlacement(): void {
    if (this.placementArmed) {
      this.disarmPlacement();
    } else {
      this.armPlacement();
    }
  }

  private armPlacement(): void {
    this.placementArmed = true;
    document.body.classList.add('is-placing-hotspot');
    if (this.addHotspotBtn) {
      this.addHotspotBtn.classList.add('is-armed');
      this.addHotspotBtn.innerHTML = '✕ Cancelar';
    }
    showToast('Clique no panorama para posicionar o hotspot (Esc para cancelar)', { tone: 'info' });
  }

  private disarmPlacement(): void {
    this.placementArmed = false;
    document.body.classList.remove('is-placing-hotspot');
    if (this.addHotspotBtn) {
      this.addHotspotBtn.classList.remove('is-armed');
      this.addHotspotBtn.innerHTML = '<span aria-hidden="true">＋</span> Hotspot';
    }
  }

  private clearGeneratedVariants(): void {
    clearAllGeneratedVariants();
    showToast('Variantes geradas removidas — recarregue a página para limpar a UI', { tone: 'info' });
  }

  private async copyProjectId(): Promise<void> {
    const id = getProjectId();
    try {
      await navigator.clipboard.writeText(id);
      showToast(`ID copiado: ${id.slice(0, 8)}… — cole em outro dispositivo com "Importar variantes"`, { tone: 'info' });
    } catch {
      showToast(`ID do projeto: ${id}`, { tone: 'info' });
    }
  }

  private async importVariants(): Promise<void> {
    const id = window.prompt('Cole o ID do projeto de outro dispositivo:')?.trim();
    if (!id || !/^[0-9a-f-]{36}$/.test(id)) {
      if (id !== null && id !== undefined) showToast('ID inválido — use o formato UUID', { tone: 'error' });
      return;
    }
    try {
      const count = await importFromServer(id, this.opts.config.rooms);
      setProjectId(id);
      if (count > 0) {
        showToast(`${count} variante(s) importada(s) — recarregue para ver`, { tone: 'info' });
      } else {
        showToast('Nenhuma variante nova encontrada para esse ID', { tone: 'info' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Erro ao importar: ${msg}`, { tone: 'error' });
    }
  }

  /**
   * Send the currently-active variant to `/api/variants/promote`. The Vercel
   * function downloads the panorama from Vercel Blob, commits it + a
   * `rooms.json` update to a new branch, and opens a PR. Once merged, the
   * variant is canonical across all devices (closes the cross-device gap
   * that localStorage persistence alone can't bridge).
   */
  private async promoteActiveVariant(): Promise<void> {
    const room = this.opts.getActiveRoom();
    const variant = this.opts.getActiveVariant();
    if (!room || !variant) {
      showToast('Promover: nenhuma variante ativa', { tone: 'error' });
      return;
    }
    if (!/^https:\/\//.test(variant.panoramaUrl)) {
      showToast('Promover: apenas variantes geradas por IA (com URL Vercel Blob) podem ser promovidas', { tone: 'error' });
      return;
    }
    if (!variant.generatedStyle) {
      showToast('Promover: variante não tem metadados de geração (re-gere via Restilizar)', { tone: 'error' });
      return;
    }
    const defaultLabel = variant.name.replace(/^IA · |^Gerado · |^Repintado · /, '');
    const label = window.prompt('Como esta variante deve aparecer no chip bar? (max 60 char)', defaultLabel)?.trim();
    if (!label) return;
    if (label.length > 60) {
      showToast('Promover: rótulo passou de 60 caracteres', { tone: 'error' });
      return;
    }

    showToast('Promovendo variante… abrindo PR no GitHub', { tone: 'info' });
    try {
      const res = await fetch('/api/variants/promote', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          roomId: room.id,
          blobUrl: variant.panoramaUrl,
          label,
          stylePreset: variant.generatedStyle,
          prompt: variant.generatedPrompt,
        }),
      });
      if (res.status === 501) {
        showToast('Save-to-Project não configurado — defina GITHUB_TOKEN no Vercel', { tone: 'error' });
        return;
      }
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        showToast(`Promover: erro ${res.status} — ${errBody.error ?? 'desconhecido'}`, { tone: 'error' });
        return;
      }
      const body = (await res.json()) as { prUrl: string; prNumber: number };
      showToast(`Variante promovida — PR #${body.prNumber} aberta`, { tone: 'info' });
      window.open(body.prUrl, '_blank', 'noopener');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Promover: falha de rede — ${msg}`, { tone: 'error' });
    }
  }

  /**
   * Photo → Room stub flow. Operator picks an image file → POST to
   * `/api/floorplan/analyze` → Claude Sonnet 4.6 vision returns a
   * structured analysis → render a modal with the generated JSON and
   * a "Copiar" button. Operator drops it into rooms.json (or uses the
   * promote flow to PR it).
   *
   * Future enhancement: hook directly into `/api/variants/promote` to
   * auto-PR a full new room.
   */
  private async analyzePhotoFlow(): Promise<void> {
    const file = await pickImageFile();
    if (!file) return;
    showToast('Analisando foto…', { tone: 'info' });

    try {
      const dataUrl = await readAsDataUrl(file);
      const base64 = dataUrl.split(',')[1] ?? '';
      const mediaType = (dataUrl.match(/^data:([^;]+);/)?.[1]) ?? 'image/jpeg';

      const res = await fetch('/api/floorplan/analyze', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType }),
      });
      if (res.status === 501) {
        showToast('Analisar foto não configurado — defina ANTHROPIC_API_KEY no Vercel', { tone: 'error' });
        return;
      }
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        showToast(`Analisar foto: erro ${res.status} — ${errBody.error ?? 'desconhecido'}`, { tone: 'error' });
        return;
      }
      const analysis = (await res.json()) as {
        name: string;
        subtitle: string;
        narration: string;
        suggestedStylePreset: string;
        detectedFeatures: string[];
      };

      // Synthesize a Room JSON stub matching the rooms.json schema.
      const stubId = analysis.name
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40) || `room_${Date.now().toString(36)}`;
      const stub = {
        id: stubId,
        name: analysis.name,
        subtitle: analysis.subtitle,
        narration: analysis.narration,
        defaultVariant: 'before',
        beforeVariantId: 'before',
        initialYaw: 0,
        initialPitch: 0,
        scale: { metresPerRadian: 2.5 },
        floorplan: { shape: [[0, 0], [6, 0], [6, 4], [0, 4]], cameraXY: [3, 2], yawOffsetDeg: 0 },
        variants: [
          { id: 'before', name: 'Antes (estado atual)', panoramaUrl: '/assets/panoramas/TODO.png', isBefore: true },
        ],
        hotspots: [],
      };

      this.showAnalysisModal(analysis, stub);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Analisar foto: ${msg}`, { tone: 'error' });
    }
  }

  private showAnalysisModal(
    analysis: { name: string; subtitle: string; narration: string; suggestedStylePreset: string; detectedFeatures: string[] },
    stub: Record<string, unknown>,
  ): void {
    const existing = document.getElementById('analyze-photo-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'analyze-photo-modal';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9200;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.78); backdrop-filter: blur(6px);
      font-family: system-ui, -apple-system, sans-serif;
    `;

    const stubJson = JSON.stringify(stub, null, 2);
    const featuresHtml = analysis.detectedFeatures.map((f) => `<span style="display:inline-block;padding:2px 8px;margin:2px;background:rgba(79,199,255,0.15);border:1px solid rgba(79,199,255,0.4);border-radius:10px;font-size:11px;color:#4fc7ff;">${escapeHtml(f)}</span>`).join('');

    overlay.innerHTML = `
      <div style="background:#0a1620;color:#fff;max-width:640px;width:90vw;max-height:88vh;overflow-y:auto;padding:24px;border-radius:12px;box-shadow:0 12px 32px rgba(0,0,0,0.5);">
        <header style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px;">
          <h2 style="margin:0;font-size:18px;font-weight:600;">📸 ${escapeHtml(analysis.name)}</h2>
          <button id="analyze-close" type="button" style="background:none;border:none;color:#fff;font-size:24px;cursor:pointer;line-height:1;">×</button>
        </header>
        <p style="margin:0 0 16px;color:#a0b0c0;font-size:13px;">${escapeHtml(analysis.subtitle)}</p>
        <h3 style="margin:12px 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:#80a0b0;">Narração</h3>
        <p style="margin:0 0 12px;font-size:13px;line-height:1.5;">${escapeHtml(analysis.narration)}</p>
        <h3 style="margin:12px 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:#80a0b0;">Preset sugerido</h3>
        <p style="margin:0 0 12px;font-size:13px;"><code style="background:rgba(255,255,255,0.08);padding:2px 8px;border-radius:4px;">${escapeHtml(analysis.suggestedStylePreset)}</code></p>
        <h3 style="margin:12px 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:#80a0b0;">Elementos detectados</h3>
        <div style="margin-bottom:16px;">${featuresHtml}</div>
        <h3 style="margin:12px 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:#80a0b0;">JSON pronto para rooms.json</h3>
        <pre id="analyze-json" style="background:#020812;border:1px solid rgba(255,255,255,0.08);padding:12px;border-radius:6px;font-size:11px;font-family:'SF Mono',Consolas,monospace;line-height:1.5;overflow-x:auto;color:#d0e0f0;">${escapeHtml(stubJson)}</pre>
        <div style="display:flex;gap:8px;margin-top:16px;">
          <button id="analyze-copy" type="button" style="padding:8px 16px;font-size:13px;font-weight:600;border:1px solid rgba(79,199,255,0.5);background:rgba(79,199,255,0.15);color:#4fc7ff;border-radius:6px;cursor:pointer;">Copiar JSON</button>
          <button id="analyze-close-2" type="button" style="padding:8px 16px;font-size:13px;border:1px solid rgba(255,255,255,0.2);background:transparent;color:#fff;border-radius:6px;cursor:pointer;">Fechar</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    document.getElementById('analyze-close')?.addEventListener('click', close);
    document.getElementById('analyze-close-2')?.addEventListener('click', close);
    document.getElementById('analyze-copy')?.addEventListener('click', () => {
      void navigator.clipboard.writeText(stubJson).then(() => {
        showToast('JSON copiado — cole em src/data/rooms.json dentro do array rooms[]', { tone: 'info' });
      });
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  }

  /**
   * Embed mode's counterpart to Export: push whatever is queued to the portal
   * right now. Edits already autosave through the debounced bridge, so this is
   * reassurance rather than mechanism — but "did that save?" is the question an
   * admin asks before closing a tab, and it deserves an answer they can click.
   */
  private saveToPortal(): void {
    this.opts.onSaveRequested?.();
    showToast('Alterações enviadas ao portal', { tone: 'info' });
  }

  private exportRoomsJson(): void {
    const json = JSON.stringify(this.opts.config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rooms.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast('rooms.json exportado — salve em src/data/ para persistir', { tone: 'info' });
  }

  /**
   * Count the arrows that point nowhere, in the toolbar, always visible.
   *
   * The property panel marks the one hotspot being edited; this is the number
   * the operator needs at the END of a session, when the panel is closed and
   * the mistake is spread over a dozen rooms they have already left. Hidden
   * entirely at zero — a permanent "0 problems" badge is noise, and noise is
   * what made "— selecione —" invisible in the first place.
   */
  refreshNavProblems(): void {
    if (!this.navProblemsBtn) return;
    const problems = findNavProblems(this.opts.config);
    this.navProblemsBtn.hidden = problems.length === 0;
    if (problems.length === 0) return;
    this.navProblemsBtn.textContent =
      problems.length === 1 ? '⚠ 1 seta sem destino' : `⚠ ${problems.length} setas sem destino`;
    this.navProblemsBtn.title =
      'Estas setas impedem a publicação. Clique para listar onde elas estão.';
  }

  /** Say WHERE they are — a count alone does not find them. */
  private reportNavProblems(): void {
    const problems = findNavProblems(this.opts.config);
    if (problems.length === 0) return;
    const shown = problems
      .slice(0, 6)
      .map((p) => `${p.roomName} → "${p.label}"`)
      .join('\n');
    const rest = problems.length - Math.min(problems.length, 6);
    showToast(`Setas sem destino:\n${shown}${rest > 0 ? `\n…e mais ${rest}` : ''}`, {
      tone: 'error',
      ms: 8000,
    });
  }
}

function isEditUrlFlagSet(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('edit') === '1';
}

function round(v: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}

/** Browser file picker for an image, returns null on cancel. Used by the
 *  📸 Analisar foto editor toolbar action. */
function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif';
    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      resolve(file);
    });
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
